import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import ApplicationContext from '../../../ApplicationContext';
import { OiMbl } from '../../../fms.core/OiMbl';
import { ApiResult, InsertResultsWithData } from '../../../framework.core/data/ApiResult';
import { JobNames } from '../../../framework.core/JobNames';
import useDirtyCheck from '../../../hooks/useDirtyCheck2';

import MblMilestoneView, { OiMblMilestoneHandle } from './implementation/MblMilestone';

import { useTypedFastStorage } from '../../../hooks/useFastStorage';

import { AddEventToNotice, CreateCargoEventLog, FindIndexOfKeyStartsWith, prepareOiMblForView } from './implementation/mblUtils';
import { OmContainer } from '../../../fms.core/OmContainer';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import { SQLField, SQLInfo, SQLType } from '../../../framework.core/data/SQLInfo';
import { historyUpdate } from '../../../fms.data/modules/FmsModFunc';
import { TrackableEntity } from '../../../fms.core/data/ChangeTrackable';
import { getSQLInfoList } from '../../../fms.core/data/TrackingModule';
import { MblStatusViewModel, OceanMblStatusMap, OiContainerEvents } from '../../../fms.core/MblStatusViewModel';
import SecondaryButton from '../../../ui/SecondaryButton';
import useSetEditedId from '../../../hooks/useSetEditedId';
import { compareIso, formatDateTime, now } from '../../../dateUtils';
import { CargoEventName } from '../../../framework.core/CargoEventName';
import { CargoEventLog } from '../../../fms.core/CargoEventLog';
import constants from '../../../constants';
import fastStorage from '../../../fastStorage';
import JobListViewContext from '../../common/JobListViewContext';

const eventMappings = [
  { prefix: "0|", name: CargoEventName.FmsBooking, seq: 9, mappingKey:'0' },
  { prefix: "1|", name: CargoEventName.EmptyOut, seq: 11, mappingKey:'1' },
  { prefix: "250", name: CargoEventName.FullIn, seq: 13, mappingKey:'250' },
  { prefix: "3|", name: CargoEventName.VesselLoaded, seq: 15, mappingKey:'15' },
  { prefix: "260,470", name: CargoEventName.VesselDeparted, seq: 16, mappingKey:'260' },
  { prefix: "5|", name: CargoEventName.CarrierRls, seq: 17, mappingKey:'5' },
  { prefix: "6|", name: CargoEventName.CustomsRls, seq: 18, mappingKey:'6' },
  { prefix: "270,460", name: CargoEventName.VesselArrived, seq: 19, mappingKey:'270' },
  { prefix: "275", name: CargoEventName.VesselDischarged, seq: 20, mappingKey:'275' },
  { prefix: "9|", name: CargoEventName.FmsDlvrToCfs, seq: 5, mappingKey:'9' },
  { prefix: "290", name: CargoEventName.RailDeparted, seq: 21, mappingKey:'290' },
  { prefix: "295", name: CargoEventName.RailArrived, seq: 22, mappingKey:'295' },
  { prefix: "12|", name: CargoEventName.PickAvail, seq: 23, mappingKey:'12' },
  { prefix: "13|", name: CargoEventName.FullOut, seq: 24, mappingKey:'13' },
  { prefix: "14|", name: CargoEventName.Delivered, seq: 25, mappingKey:'14' },
  { prefix: "15|", name: CargoEventName.EmptyIn, seq: 26, mappingKey:'15' },
];

function getStorageKey(id: string) {
  return `oi-mbl-bl-status-${id}`;
}

function MblMilestoneImpl({
  mblId,
  storageKey,
  isView,
  onClose,
}: {
  mblId: number,
  storageKey: string,
  isView: boolean,
  onClose: (x:OiMbl|null)=> void,
}) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const setSavedEntity = useSetEditedId([JobNames.OIContainer]);

  const [initialized, setInitialized] = useState(false);
  
  const [mbl, setMbl, removeMbl, mblRef] = useTypedFastStorage<OiMbl>(storageKey, null);
  const [/*blStatus*/, /*setBlStatus*/, removeBlStatus, /*blStatusRef*/] = useTypedFastStorage<MblStatusViewModel>(getStorageKey(mblId.toString()), null);
  const {setCleanState} = useDirtyCheck(
    storageKey,
    ['updated', 'updatedBy']);
    
  const jobListContext = useContext(JobListViewContext);
  
  useEffect(() => {
    initialize();
    return () => {
      cleanup();
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function doClose(notifyResult: boolean = false) {
    //cleanup();
    onClose(notifyResult ? mblRef.current : null);
  }
  
  function cleanup() {
    removeMbl();
    removeBlStatus();
  }

  async function initialize() {
    const apiResult:ApiResult<OiMbl> = await appContext.api.getJobDetail(JobNames.OIMbl, { id: mblId});
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog('Cannot load data');
      doClose();
      return;
    }
    const loadedMbl =  new OiMbl(apiResult.result);

    const blApiResult: ApiResult<MblStatusViewModel> = await appContext.api.getMblStatusViewModel(JobNames.OIMbl, mblId);
    loadedMbl.milestone = new MblStatusViewModel(blApiResult?.result||null);
    prepareOiMblForView(loadedMbl);

    loadedMbl.milestoneContainerSelectedIndex = loadedMbl.oimContainerList && loadedMbl.oimContainerList.length > 0
      ? 0
      : -1;

    const loadedModelObj = new OiMbl(_.cloneDeep(loadedMbl));
    setCleanState(loadedModelObj);
    setMbl(loadedModelObj);
    if(mblRef.current !== null) {
      mblRef.current.trackable = true;
    }

    setInitialized(true);
  }

  // MContainer와 일치하는 항목 업데이트
  function RefreshListView(keyValue: number, value: string|null) {
    if (!value || keyValue <= 0) return;

    const keyMatch = _.keys(fastStorage).find(x => x.startsWith('ifms-list-data-/common/joblistview/MYEVT/myEvent'));
    if (keyMatch) {
      const listData = _.values(fastStorage[keyMatch]);
      listData.filter(x => x.mCntrId === keyValue).forEach(row => {
        row.carrierRlsDate = value;
        jobListContext.updateDataRow(row);
      });
    }
  }

  async function save() {
    if (!mbl || !mblRef.current) {
      throw new Error('Invalid operation');
    }

    if(!mblRef.current.isDirty) {
      //await showGlobalOkDialog(<p>Nothing to save!!!</p>);
      doClose();
      return;
    }

    const si:SQLInfo[] = [];

    let hiSqlInfo = historyUpdate("T_OIMMAIN", mblRef.current?.id??0, appContext.user?.id??'', 'Update B/L Info');
    if (hiSqlInfo) {
      si.push(hiSqlInfo);
    }

    const blStatusInfoList = getSQLInfoList(mbl as TrackableEntity);
    blStatusInfoList.push(...si);

    // MBL > Carrier Release 날짜가 없는 경우 Container Status에 입력한 최소 값으로 업데이트
    let cntrId = 0, prevCntrId = 0;
    let carrierRlsDate = '', prevCarrierRlsDate = '';

    blStatusInfoList.filter(x => x.tableName === "T_OIMCONTAINER").forEach(cntr => {
      const carrierRls = cntr?.fields.find(f => f.fieldName === "f_carrierrls");
      if (carrierRls) {
        carrierRlsDate = carrierRls.value;
        cntrId = cntr.key?.value??0;

        // My Evnet List 갱신
        RefreshListView(cntrId, carrierRlsDate);

        if (prevCarrierRlsDate && compareIso(carrierRlsDate, prevCarrierRlsDate) > 0) {
          carrierRlsDate = prevCarrierRlsDate;
          cntrId = prevCntrId;
        }
        prevCarrierRlsDate = carrierRlsDate;
        prevCntrId = cntr.key?.value??0;
      }
    });

    if (!mblRef.current?.rlsDate && cntrId) {
      const sqlInfo = new SQLInfo ({
        tableName: "T_OIMMAIN",
        type: SQLType.Update,
        fields: [
          new SQLField({ fieldName: "F_RLSDate", propertyName: "rlsDate", value: carrierRlsDate }),
          new SQLField({ fieldName: "rlsby", propertyName: "rlsDateBy", value: appContext.user?.id??'' }),
          new SQLField({ fieldName: "F_U2ID", propertyName: "u2Id", value: appContext.user?.id??'' }),
          new SQLField({ fieldName: "F_U2Date", propertyName: "u2Date", value: now() }),
        ],
        key: new SQLField({ fieldName: "F_ID", propertyName: "Id", value: mblRef.current?.id??0 })
      });
      blStatusInfoList.push(sqlInfo);

      setMbl({ rlsDate: carrierRlsDate, rlsDateBy: appContext.user?.id??''});
    }
    

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveMblStatusViewModel(blStatusInfoList);
    
    if (apiResult && apiResult.success) {
      await UpdateLogMblMilestone(blStatusInfoList, mbl);    //[t49-webhook] LogMblMilestone. 이 기능은 TrackingService를 사용하지 않더라도 적용한다.
      await showGlobalOkDialog(<p>The data has been saved</p>);
      setSavedEntity(mblRef.current.id.toString(), false);
    } else {
      await showGlobalOkDialog(<div>
        <p>Cannot save</p>
        <p>{apiResult?.message}</p>
      </div>);
    }

    doClose(true);
  }

  async function UpdateLogMblMilestone(blStatusInfoList: SQLInfo[], mbl:OiMbl) {
    const cntrDataWithSeq: {
      loc: string,
      cntrNo: string,
      seq: number,
    }[] = []

    const dicEvent = CollectMblEvents(blStatusInfoList, cntrDataWithSeq);
    if (dicEvent.length > 0) {
      const cmile: CargoEventLog[] = [];
      const lastEvents: CargoEventLog[] = [];

      for (const mapping of eventMappings) {
        const dindex = FindIndexOfKeyStartsWith(dicEvent, mapping.prefix);
        if (dindex >= 0) {
          const cargoEventLog = CreateCargoEventLog(mbl.mblNo, mapping.name, dicEvent[dindex], cntrDataWithSeq.filter(x => x.seq === mapping.seq), appContext.user?.id ?? '');
          cmile.push(...cargoEventLog);
        }
      }

      const uniqueCmileSet = new Set(cmile.map(item => item.cntrNo));
      Array.from(uniqueCmileSet).forEach(x => {
        const tempData = cmile.filter(c => c.cntrNo === x).sort((a, b) => new Date(a.utcTime ?? '').getTime() - new Date(b.utcTime ?? '').getTime());
        lastEvents.push(tempData[tempData.length - 1]);
      });

      if (lastEvents.length > 0) {
        const apiResult = await appContext.api.trackingLogMblEvents(JobNames.OIMbl, mblId, lastEvents);;
        if (!apiResult || !apiResult.success) {
          await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
        }
      }
    }
  }

  function CollectMblEvents(blStatusInfoList: SQLInfo[], cntrDataWithSeq: { loc: string, cntrNo: string, seq: number }[]) {
    const changeData: { pimsCntrId: number, seqNum: number, dateTime: string }[] = [];
    const dicEvent: { key: string, value: string }[] = [];

    const changeContainerData = blStatusInfoList.filter(x => x.tableName === 'T_OIMCONTAINER');

    OiContainerEvents.forEach((enumEvent) => {
      changeContainerData.forEach(x => {
        const findData = x.fields.find(x => x.propertyName.endsWith(OceanMblStatusMap.get(enumEvent)?.cntrDate ?? ''));
        if (findData) {
          const pimsCntrId = mbl?.oimContainerList.find(y => y.id === x.key?.value)?.pimsLinkId ?? 0;
          changeData.push({ pimsCntrId: pimsCntrId, seqNum: enumEvent, dateTime: formatDateTime(findData.value) });

          const containerNo = mbl?.oimContainerList.find(y => y.id === x.key?.value)?.containerNo ?? ''; //GetLocNameFromEvent
          const cntr = mbl?.oimContainerList.find(x => x.containerNo === containerNo);
          const cntrLoc = OceanMblStatusMap.get(enumEvent)?.cntrLoc ?? '';

          cntrDataWithSeq.push({ loc: Object.getOwnPropertyDescriptor(cntr, cntrLoc)?.value, seq: enumEvent, cntrNo: containerNo })
        }
      })
    });
    changeData.forEach(x => {
      const seqNum = x.seqNum;
      const mappingKey = eventMappings.find(x => x.seq === seqNum)?.mappingKey;
      if (mappingKey)
        AddEventToNotice(dicEvent, `${mappingKey}|${x.dateTime}`, String(x.pimsCntrId));
    });

    return dicEvent;
  }

  return (
    <div className='report-popup'>
      {!initialized &&
        <div style={{position: 'relative', top: '-25px'}}>
          <SkeletonTheme color="#4287f5" highlightColor="#b9d2fa">
            <Skeleton count={1} height={2} />
          </SkeletonTheme>
        </div>
      }

      {!!mbl &&
        <MblMilestoneInitializedImpl mbl={mbl} setMbl={setMbl} isView={isView} save={save} close={doClose} />
      }
    </div>
  );
}

function MblMilestoneInitializedImpl({
  mbl,
  setMbl,
  isView,
  save,
  close,
}: {
  mbl: OiMbl,
  setMbl: (x:Partial<OiMbl>) => void,
  isView: boolean,
  save: () => Promise<void>,
  close: VoidFunction,
}) {
  const appContext = useContext(ApplicationContext);
  const milestoneRef = useRef<OiMblMilestoneHandle>(null);

  const containerInfo:OmContainer[] = mbl.oimContainerList ?? [];

  function changeContainerInfo(ci:OmContainer[]) {
    setMbl({ oimContainerList: ci });
  }

  async function applyToAll() {
    if (milestoneRef.current) {
      milestoneRef.current.executeApplyToAll();
      await save();
    }
  }

  return (
    <>
      <MblMilestoneView
        ref={milestoneRef}
        mbl={mbl}
        onChange={setMbl}
        isView={isView}
        containerInfo={containerInfo}
        onContainerInfoChange={changeContainerInfo}
        user={appContext.user?.id ?? ''}
      />
      <div className='report-footer'>
        <SecondaryButton
          name="oi-Milestone-apply-to-all-container-button"
          size="regular"
          disabled={isView || mbl.milestoneContainerSelectedIndex < 0}
          onClick={applyToAll}>
          Apply to All Container
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          name="oi-Milestone-update-button"
          disabled={isView || mbl.milestoneContainerSelectedIndex < 0}
          onClick={save}>
          Update
        </SecondaryButton>
        <SecondaryButton
          name="oi-Milestone-cancel-button"
          size="regular"
          onClick={close}>
          Cancel
        </SecondaryButton>
      </div>
    </>
  );
}

const MblMilestonePopup = ({
  mblId,
  storageKey,
  isView,
  onClose,
}:{
  mblId: number,
  storageKey: string,
  isView: boolean,
  onClose: (x:OiMbl|null)=> void,
}) => {
  return (
    <MblMilestoneImpl mblId={mblId} key={storageKey} storageKey={storageKey} onClose={onClose} isView={isView} />
  );
};

MblMilestonePopup.defaultProps = {
};

MblMilestonePopup.propTypes = {
  mblId: PropTypes.number.isRequired,
  storageKey: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  isView: PropTypes.bool.isRequired,
};

export default React.memo(MblMilestonePopup);


